import { useState } from "react";
import { Button, Dialog, Notice, TextField } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { PinPad } from "../ui/PinPad";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro, anteilCent } from "@shared/geld";
import type { GeraetDaten, WarenkorbAnsicht } from "./api";
import { RABATT_GRUND_TEXT, useRabatt, type RabattGrund } from "./sonderfaelle-api";

/** Rabatt-Dialog: Position wählen, neuer Preis oder Prozent, Grund; bei Freigabepflicht PIN eines Berechtigten. */
export function RabattDialog({ offen, onClose, warenkorb, daten }: { offen: boolean; onClose: () => void; warenkorb: WarenkorbAnsicht; daten: GeraetDaten }) {
  const rabatt = useRabatt();
  const artikelPositionen = warenkorb.positionen.filter((p) => p.art === "artikel");
  const [posId, setPosId] = useState<string | null>(artikelPositionen[0]?.id ?? null);
  const [art, setArt] = useState<"prozent" | "preis">("prozent");
  const [prozent, setProzent] = useState("10");
  const [preis, setPreis] = useState("");
  const [grund, setGrund] = useState<RabattGrund>("maengel");
  const [fehler, setFehler] = useState<string | null>(null);
  const [freigabe, setFreigabe] = useState<{ benutzerId: string } | null>(null);
  const pos = artikelPositionen.find((p) => p.id === posId) ?? artikelPositionen[0];
  const freigeber = (daten.personen ?? []).filter((p) => p.pinGesetzt && (p.rolle === "inhaber" || p.rolle === "mitarbeiter"));

  const wert = art === "prozent" ? Math.max(0, Math.min(100, Number(prozent.replace(",", ".")) || 0)) : (parseEuro(preis) ?? -1);
  const neuerPreis = pos ? (art === "prozent" ? pos.preisCent - anteilCent(pos.preisCent, wert) : wert) : 0;
  const gueltig = !!pos && (art === "prozent" ? wert >= 0 && wert <= 100 : wert >= 0 && wert <= pos.preisCent);

  async function absenden(pin?: string) {
    if (!pos || !gueltig) return;
    setFehler(null);
    try {
      await rabatt.mutateAsync({ id: warenkorb.id, pos: pos.id, art, wert, grund, freigabe: pin && freigabe ? { benutzerId: freigabe.benutzerId, pin } : undefined });
      onClose();
      setFreigabe(null);
    } catch (err) {
      if (err instanceof ApiFehler && err.status === 428) {
        setFreigabe({ benutzerId: freigeber[0]?.id ?? "" });
        return;
      }
      setFehler(err instanceof ApiFehler ? err.message : "Rabatt konnte nicht gesetzt werden.");
    }
  }

  if (!pos) {
    return (
      <Dialog open={offen} title="Rabatt geben" onClose={onClose} size="sm" actions={<Button variant="quiet" onClick={onClose}>Schließen</Button>}>
        <Notice tone="info">Rabatt gibt es nur auf Artikel. Der Warenkorb enthält noch keinen.</Notice>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={offen}
      title="Rabatt geben"
      onClose={onClose}
      actions={
        freigabe ? (
          <Button variant="quiet" onClick={() => setFreigabe(null)}>
            Zurück
          </Button>
        ) : (
          <>
            <Button variant="quiet" onClick={onClose}>
              Abbrechen
            </Button>
            <Button variant="primary" icon="percent" disabled={!gueltig} loading={rabatt.isPending} onClick={() => void absenden()}>
              Rabatt übernehmen
            </Button>
          </>
        )
      }
    >
      <div className="app-form">
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        {artikelPositionen.length > 1 ? (
          <div className="sc-col" style={{ gap: 0 }}>
            <span className="rg-field__label">Artikel</span>
            {artikelPositionen.map((p) => (
              <ItemRow key={p.id} title={p.bezeichnung} photo={false} meta={[p.regalNummer && `Regal ${p.regalNummer}`, p.code].filter(Boolean).join(" · ")} price={p.preisCent} selected={p.id === pos.id} onClick={() => setPosId(p.id)} />
            ))}
          </div>
        ) : (
          <ItemRow title={pos.bezeichnung} photo={false} meta={[pos.regalNummer && `Regal ${pos.regalNummer}`, pos.code, pos.mieterName].filter(Boolean).join(" · ")} price={pos.preisCent} />
        )}
        {freigabe ? (
          <>
            <Notice tone="attention" title="Freigabe durch Inhaber oder Mitarbeiter">
              Dieser Rabatt ({art === "prozent" ? `${wert} %` : formatEuro(neuerPreis)}) braucht eine Freigabe. Die Person wählt sich aus und tippt ihre PIN ein.
            </Notice>
            <div className="sc-toolbar">
              {freigeber.map((p) => (
                <button key={p.id} type="button" className="sc-pill" aria-pressed={p.id === freigabe.benutzerId} onClick={() => setFreigabe({ benutzerId: p.id })}>
                  {p.name}
                </button>
              ))}
            </div>
            <PinPad label={`PIN von ${freigeber.find((p) => p.id === freigabe.benutzerId)?.name ?? "…"}`} error={fehler ?? undefined} disabled={rabatt.isPending} onComplete={(pin) => void absenden(pin)} />
          </>
        ) : (
          <>
            <div className="sc-toolbar" role="radiogroup" aria-label="Art">
              <button type="button" className="sc-pill" aria-pressed={art === "preis"} onClick={() => setArt("preis")}>
                Neuer Preis
              </button>
              <button type="button" className="sc-pill" aria-pressed={art === "prozent"} onClick={() => setArt("prozent")}>
                Prozent
              </button>
              <span className="sc-grow" />
              <span className="sc-strong">
                Neuer Preis {formatEuro(Math.max(0, neuerPreis))} <span className="sc-muted">(−{formatEuro(Math.max(0, pos.preisCent - neuerPreis))})</span>
              </span>
            </div>
            {art === "prozent" ? (
              <>
                <TextField label="Rabatt in Prozent" value={prozent} onChange={(e) => setProzent(e.target.value)} inputMode="decimal" suffix="%" autoFocus />
                <div className="sc-toolbar">
                  {[5, 10, 20, 50].map((p) => (
                    <button key={p} type="button" className="sc-pill" aria-pressed={Number(prozent) === p} onClick={() => setProzent(String(p))}>
                      {p} %
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <TextField label="Neuer Preis" value={preis} onChange={(e) => setPreis(e.target.value)} inputMode="decimal" suffix="€" placeholder="0,00" autoFocus error={preis && !gueltig ? "Zwischen 0,00 € und dem Originalpreis." : undefined} />
            )}
            <div className="sc-col" style={{ gap: 6 }}>
              <span className="rg-field__label">Grund</span>
              <div className="sc-toolbar" role="radiogroup" aria-label="Grund">
                {(Object.keys(RABATT_GRUND_TEXT) as RabattGrund[]).map((g) => (
                  <button key={g} type="button" className="sc-pill" aria-pressed={grund === g} onClick={() => setGrund(g)}>
                    {RABATT_GRUND_TEXT[g]}
                  </button>
                ))}
              </div>
            </div>
            <Notice tone="info">Der Mieter sieht den Rabatt mit Grund in seiner Abrechnung. Ab {daten.einstellungen?.rabattFreigabeAbProzent ?? 20} % oder bei Aushilfen bestätigt ein Inhaber oder Mitarbeiter per PIN.</Notice>
          </>
        )}
      </div>
    </Dialog>
  );
}
